import {AfterViewInit, Component, ElementRef, inject, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MapService} from "../../services/impact-map/impact-map.service";
import {ModalController} from "@ionic/angular";
import mapboxgl from "mapbox-gl";
import {LngLatModel} from "../../models/map.models";
import {StorageService} from "../../services/storage/storage.service";
import {Router} from "@angular/router";
import {GlobalService} from "../../services/global/global.service";
import {environment} from "../../../environments/environment";

interface MapboxFeature {
  id: string;
  place_name: string;
  center: [number, number];
  place_type?: string[];
}

interface MapGeocodeResponse {
  type: string;
  query: string[];
  features: MapboxFeature[];
  attribution: string;
}

@Component({
  selector: 'app-map-select-location-modal',
  templateUrl: './map-select-location-modal.component.html',
  styleUrls: ['./map-select-location-modal.component.scss'],
  standalone: false
})

export class MapSelectLocationModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapEl', {static: false}) mapEl!: ElementRef<HTMLDivElement>;
  @Input('type') type: string = '';
  @Input('id') id: string | number = '';
  @Input('position') position: LngLatModel | undefined;
  @Input('markerSet') markerSet: LngLatModel | undefined;
  @Input('editable') editable: boolean = true;
  @Input('initialCreation') initialCreation?: boolean = false;


  private MAPBOX_TOKEN: string = environment.mapboxAccessTokenWeb;


  private map!: mapboxgl.Map;
  private marker!: mapboxgl.Marker;
  private zoom: number = 18;

  markerPoints: LngLatModel | null = null;
  showOverlay: boolean = false;
  isMarkerSet: boolean = false;

  public isWeb: boolean = false;

  private globalService: GlobalService = inject(GlobalService);

  public searchResults: MapboxFeature[] = [];
  private searchAbort?: AbortController;


  constructor(
    private mapService: MapService,
    private modalController: ModalController,
    private storage: StorageService,
    private router: Router
  ) {
  }

  async ngOnInit() {
    this.isWeb = this.globalService.isWeb;
    const overlayShown: string = await this.storage.getItem('setLocationOverlayShown');
    this.showOverlay = overlayShown !== 'true';
    this.zoom = this.mapService.getMapZoom();

    if (this.position instanceof Promise) {
      this.position = await this.position;
    }
  }

  ngOnDestroy() {
    if (this.map) {
      try {
        this.map.remove();
      } catch {
      }
    }
  }

  ionViewWillEnter() {
    const saved: LngLatModel = this.mapService.getSelectedMarker();
    if (saved) {
      this.markerSet = saved;
      this.isMarkerSet = true;
    } else if (this.markerSet?.lng && this.markerSet?.lat) {
      this.isMarkerSet = true;
    }
  }

  ngAfterViewInit() {
    if (!this.mapEl?.nativeElement) return;
    let initLng: number = this.position && this.position.lng ? this.position.lng : 11.3974;
    let initLat: number = this.position && this.position.lat ? this.position.lat : 47.26923;

    if (this.markerSet && this.markerSet.lng && this.markerSet.lat) {
      initLng = this.markerSet.lng;
      initLat = this.markerSet.lat;
    }

    this.map = this.mapService.initMap(this.mapEl.nativeElement, [initLng, initLat], this.zoom, true);

    this.map.on('load', () => {
      this.mapService.safeResizeSoon();
      if (this.position) {
        this.setCurrentPosition(this.position.lng, this.position.lat)
      }
      if (this.markerSet && this.markerSet.lng && this.markerSet.lat) {
        this.setOrMoveMarker([this.markerSet.lng, this.markerSet.lat]);
      }
    });

    if (this.editable) {
      this.map.on('click', (event) => {
        const coords: [number, number] = [event.lngLat.lng, event.lngLat.lat];
        this.setOrMoveMarker(coords);
      });
    }
  }

  private setOrMoveMarker(coords: [number, number]): void {
    this.isMarkerSet = true;

    if (this.marker) {
      this.marker.setLngLat(coords);
    } else {
      this.marker = new mapboxgl.Marker({draggable: true, color: '#00373d'})
        .setLngLat(coords)
        .addTo(this.map);

      this.marker.on('dragend', () => {
        const lngLat: mapboxgl.LngLat = this.marker.getLngLat();
        this.markerPoints = {lng: lngLat.lng, lat: lngLat.lat};
        this.mapService.setSelectedMarker(this.markerPoints);
      });
    }

    this.markerPoints = {lng: coords[0], lat: coords[1]};
    this.mapService.setSelectedMarker(this.markerPoints);
  }

  private setCurrentPosition(lng: number, lat: number): void {
    if (!this.map.getSource('user-location')) {
      this.map.addSource('user-location', {
        type: 'geojson',
        data: {
          properties: {},
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lng, lat]
          }
        }
      });

      // Add a circle layer (blue dot)
      this.map.addLayer({
        id: 'user-location-circle',
        type: 'circle',
        source: 'user-location',
        paint: {
          'circle-radius': 8,
          'circle-color': '#007AFF',   // iOS blue
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff'
        }
      });
    } else {
      // Update source data if it already exists
      const source = this.map.getSource('user-location') as mapboxgl.GeoJSONSource;
      source.setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      });
    }
  }

  flyToPosition() {
    this.map.flyTo({
      center: [this.position.lng, this.position.lat],
      zoom: this.zoom,
      essential: true
    });
  }

  removeMarker() {
    this.isMarkerSet = false;
    this.markerPoints = undefined;

    if (this.marker) {
      this.marker.remove();
      this.marker = undefined;
    }

    this.mapService.setSelectedMarker(null);
  }


  async closeModal() {
    await this.modalController.dismiss(undefined, 'cancel')
  }

  async setMarker() {
    await this.modalController.dismiss(this.markerPoints, 'submit')
  }


  public async hideOverlay() {
    this.showOverlay = false;
    await this.storage.setItem('setLocationOverlayShown', 'true');
  }

  public async showOnMap(): Promise<void> {
    const coords: LngLatModel =
      this.markerPoints ||
      this.markerSet ||
      this.mapService.getSelectedMarker();

    if (!coords) {
      await this.modalController.dismiss(undefined, 'cancel');
      return;
    }

    if (this.globalService.isMobile()) {
      await this.modalController.dismiss(undefined, 'showOnMap');
      await this.router.navigate(['/app/impact-map'], {
        queryParams: {type: this.type, id: this.id}
      });
    } else {
      await this.modalController.dismiss(undefined, 'cancel');
    }

    this.mapService.triggerFlyTo(coords.lng, coords.lat);
  }

  /** =======================
   *   Address search
   *  ======================= */
  public trackSearch: (_: number, item: MapboxFeature) => string = (_: number, item: MapboxFeature) => item.id;


  public async onSearchChange(ev: CustomEvent) {
    if (!this.globalService.isBrowser) return;

    const q = (ev as any)?.detail?.value?.trim?.() ?? '';
    if (!q) {
      this.searchResults = [];
      this.cancelInFlightSearch();
      return;
    }

    this.cancelInFlightSearch();
    this.searchAbort = new AbortController();

    try {
      const center = this.getMapCenter();
      const url = new URL(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(q)}.json`
      );
      url.searchParams.set('access_token', this.MAPBOX_TOKEN);
      url.searchParams.set('autocomplete', 'true');
      url.searchParams.set('limit', '8');
      url.searchParams.set('language', 'en');
      if (center) url.searchParams.set('proximity', `${center[0]},${center[1]}`);

      const res = await fetch(url.toString(), {
        signal: this.searchAbort.signal,
        headers: {Accept: 'application/json'},
      });
      if (!res.ok) {
        console.error('Geocoding failed', res.status);
        this.searchResults = [];
        return;
      }
      const data = (await res.json()) as MapGeocodeResponse;
      this.searchResults = data.features ?? [];
    } catch (e: any) {
      if (e?.name !== 'AbortError') {
        console.error('Mapbox geocoding error:', e);
        this.searchResults = [];
      }
    }
  }

  clearSearch(): void {
    this.searchResults = [];
    this.cancelInFlightSearch();
  }

  private cancelInFlightSearch(): void {
    try {
      this.searchAbort?.abort();
    } catch {
    }
    this.searchAbort = undefined;
  }

  private getMapCenter(): [number, number] | undefined {
    try {
      if (this.map && typeof this.map.getCenter === 'function') {
        const c = this.map.getCenter();
        if (c && typeof c.lng === 'number' && typeof c.lat === 'number') {
          return [c.lng, c.lat];
        }
      }
    } catch {
    }
    return undefined;
  }

  public selectSearchResult(f: MapboxFeature): void {
    this.searchResults = [];
    if (!this.map || !this.globalService.isBrowser) return;

    const [lng, lat] = f.center;

    try {
      this.map.flyTo({
        center: [lng, lat],
        zoom: 18,
        essential: true
      });

    } catch (err) {
      console.error('FlyTo failed', err);
    }
  }

}
